// SOURCE OF TRUTH: keep in sync with journal-dashboard/src/contexts/ThemeProvider.tsx
import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { tokensFor, DT_VAR_MAP, type ThemeMode } from '../styles/sproutTokens';

const STORAGE_KEY = 'cds-theme';

interface ThemeContextValue {
  mode: ThemeMode;
  toggle: () => void;
  setMode: (m: ThemeMode) => void;
}

const ThemeCtx = createContext<ThemeContextValue>({
  mode: 'light',
  toggle: () => {},
  setMode: () => {},
});

export const useTheme = () => useContext(ThemeCtx);

/**
 * Apply a theme to <html>: set data-theme + the `dark` class (so the CSS-var
 * bridge and Tailwind `darkMode:'class'` stay in agreement), and write every
 * --dt-* var as a belt-and-suspenders fallback to the CSS-defined blocks.
 */
function applyMode(mode: ThemeMode) {
  const root = document.documentElement;
  root.setAttribute('data-theme', mode);
  root.classList.toggle('dark', mode === 'dark');
  root.style.colorScheme = mode;
  const tokens = tokensFor(mode);
  for (const [cssVar, key] of Object.entries(DT_VAR_MAP)) {
    root.style.setProperty(cssVar, tokens[key]);
  }
}

function initialMode(defaultMode: ThemeMode): ThemeMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY) as ThemeMode | null;
    if (stored === 'light' || stored === 'dark') return stored;
  } catch { /* ignore */ }
  return defaultMode;
}

export function ThemeProvider({
  children,
  defaultMode = 'light',
}: {
  children: ReactNode;
  defaultMode?: ThemeMode;
}) {
  const [mode, setModeState] = useState<ThemeMode>(() => initialMode(defaultMode));

  useEffect(() => {
    applyMode(mode);
    try { localStorage.setItem(STORAGE_KEY, mode); } catch { /* ignore */ }
  }, [mode]);

  const setMode = useCallback((m: ThemeMode) => setModeState(m), []);
  const toggle = useCallback(() => setModeState((m) => (m === 'light' ? 'dark' : 'light')), []);

  return <ThemeCtx.Provider value={{ mode, toggle, setMode }}>{children}</ThemeCtx.Provider>;
}
